import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerProjectAccess } from '../../hooks/useCustomerProjectAccess';
import { useFlash } from '../../context/flashcontext';
import StyledModalView from '../Modal/StyledModalView';
import api from '../../Api';

const CustomerAccessManager = ({ projectId }) => {
  const { t } = useTranslation();
  const { grantAccess, revokeAccess, getProjectCustomers, loading } = useCustomerProjectAccess();
  const { showFlash } = useFlash();

  const [projectCustomers, setProjectCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [customerToRevoke, setCustomerToRevoke] = useState(null);

  // Fetch project customers and all customer users
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        // Get customers with access to this project
        const customers = await getProjectCustomers(projectId);
        setProjectCustomers(customers);

        // Get all customer users (role = 'klant')
        const usersResponse = await api.get('/users');
        const customerUsers = usersResponse.data.filter(user => user.role === 'klant');
        setAllCustomers(customerUsers);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        showFlash(t('errors.loadFailed') || 'Failed to load customer data', 'error');
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [projectId, getProjectCustomers, showFlash, t]);

  // Get customers who don't have access yet
  const availableCustomers = allCustomers.filter(
    customer => !projectCustomers.find(pc => pc.id === customer.id)
  );

  const handleGrantAccess = async () => {
    if (!selectedCustomerId) {
      showFlash(t('customerAccess.selectCustomer') || 'Please select a customer', 'warning');
      return;
    }

    try {
      await grantAccess(projectId, parseInt(selectedCustomerId));
      showFlash(t('customerAccess.accessGranted') || 'Access granted successfully', 'success');

      // Refresh the lists
      const customers = await getProjectCustomers(projectId);
      setProjectCustomers(customers);
      setSelectedCustomerId('');
      setShowGrantForm(false);
    } catch (error) {
      console.error('Error granting access:', error);
      showFlash(t('customerAccess.grantFailed') || 'Failed to grant access', 'error');
    }
  };

  const requestRevokeAccess = (customer) => {
    setCustomerToRevoke(customer);
    setShowRevokeModal(true);
  };

  const confirmRevokeAccess = async () => {
    if (!customerToRevoke) return;

    try {
      await revokeAccess(projectId, customerToRevoke.id);
      showFlash(t('customerAccess.accessRevoked') || 'Access revoked successfully', 'success');

      // Refresh the list
      const customers = await getProjectCustomers(projectId);
      setProjectCustomers(customers);
      setShowRevokeModal(false);
      setCustomerToRevoke(null);
    } catch (error) {
      console.error('Error revoking access:', error);
      showFlash(t('customerAccess.revokeFailed') || 'Failed to revoke access', 'error');
      setShowRevokeModal(false);
      setCustomerToRevoke(null);
    }
  };

  const cancelRevokeAccess = () => {
    setShowRevokeModal(false);
    setCustomerToRevoke(null);
  };

  if (loadingData) {
    return <div className="loading">{t('common.loading') || 'Loading...'}</div>;
  }

  return (
    <div className="employees-section">
      <h3>{t('customerAccess.title') || 'Customer Access'}</h3>

      {/* Grant Access Button/Form - matching add employee pattern */}
      {!showGrantForm ? (
        <button
          type="button"
          className="btn btn-add"
          onClick={() => setShowGrantForm(true)}
          disabled={availableCustomers.length === 0}
        >
          {t('customerAccess.grantAccess') || 'Grant Customer Access'}
        </button>
      ) : (
        <div className="add-employee-card">
          <h3>{t('customerAccess.grantAccessTitle') || 'Grant Access to Customer'}</h3>
          <div className="employee-form-grid">
            <div className="form-group-modern">
              <label>{t('customerAccess.selectCustomer')}</label>
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="form-input"
              >
                <option value="">
                  {t('customerAccess.selectCustomer') || '-- Select Customer --'}
                </option>
                {availableCustomers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="employee-form-actions">
            <button
              type="button"
              className="btn btn-accept"
              onClick={handleGrantAccess}
              disabled={!selectedCustomerId || loading}
            >
              {t('customerAccess.grantAccess') || 'Grant Access'}
            </button>
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => {
                setShowGrantForm(false);
                setSelectedCustomerId('');
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Current Access List - matching employee list pattern */}
      {projectCustomers.length > 0 ? (
        <div className="employees-grid">
          {projectCustomers.map(customer => (
            <div key={customer.id} className="employee-card">
              <div className="employee-card-header">
                <div>
                  <div className="employee-name">{customer.name}</div>
                  <div className="employee-role">{t('user.roles.klant') || 'Customer'}</div>
                </div>
              </div>

              <div className="employee-details">
                <div className="employee-detail-row">
                  <span className="employee-detail-label">{t('common.email')}</span>
                  <span className="employee-detail-value">{customer.email}</span>
                </div>
                {customer.company_name && (
                  <div className="employee-detail-row">
                    <span className="employee-detail-label">{t('customers.company')}</span>
                    <span className="employee-detail-value">{customer.company_name}</span>
                  </div>
                )}
              </div>

              <div className="employee-actions-card">
                <button
                  type="button"
                  className="btn btn-delete"
                  onClick={() => requestRevokeAccess(customer)}
                  disabled={loading}
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        !showGrantForm && (
          <div className="no-employees-message">
            {t('customerAccess.noCustomersWithAccess') || 'No customers have access to this project yet'}
          </div>
        )
      )}

      {/* Revoke Access Confirmation Modal */}
      <StyledModalView
        isOpen={showRevokeModal}
        onClose={cancelRevokeAccess}
        title={t('customerAccess.revokeAccessTitle') || 'Revoke Customer Access'}
        confirmText={t('customerAccess.revokeAccess') || 'Revoke Access'}
        closeText={t('common.cancel')}
        onConfirm={confirmRevokeAccess}
      >
        <p>
          {t('customerAccess.confirmRevokeMessage', {
            name: customerToRevoke?.name
          }) || `Are you sure you want to revoke access for ${customerToRevoke?.name}?`}
        </p>
      </StyledModalView>
    </div>
  );
};

export default CustomerAccessManager;
